import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../../context";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useQueryClient } from "@tanstack/react-query";

const EncounterDialog = ({ open, onClose, patientId, doctorId, ticketId = null }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const queryClient = useQueryClient();

  // Debug: Log patientId
  React.useEffect(() => {
    if (open && !patientId) {
      console.error("EncounterDialog: patientId is missing!", { patientId, doctorId, ticketId });
    }
  }, [open, patientId, doctorId, ticketId]);

  const { data: departmentsData } = useFetchObjects(
    ["departments"],
    "departments/",
    token
  );

  const { handleAdd, loading, resetForm } = useAdd(
    "encounters",
    token,
    t("profile.encounterAdded") || "Encounter created successfully",
    t("profile.encounterAddFailed") || "Failed to create encounter"
  );

  const [formData, setFormData] = useState({
    encounter_type: 1, // CONSULTATION
    encounter_date: new Date().toISOString().slice(0, 16),
    department: null,
    chief_complaint: "",
    diagnosis: "",
    notes: "",
    status: 1, // IN_PROGRESS
  });
  const [errors, setErrors] = useState({});

  const departments = departmentsData?.results || departmentsData || [];

  useEffect(() => {
    if (resetForm) {
      setFormData({
        encounter_type: 1,
        encounter_date: new Date().toISOString().slice(0, 16),
        department: null,
        chief_complaint: "",
        diagnosis: "",
        notes: "",
        status: 1,
      });
      setErrors({});
      onClose();
      queryClient.invalidateQueries(["unified-ehr", patientId]);
    }
  }, [resetForm, onClose, queryClient, patientId]);

  const handleSubmit = () => {
    // Validate patientId
    if (!patientId) {
      setErrors({ patient: t("profile.patientRequired") || "Patient ID is required" });
      return;
    }

    const submitData = {
      patient: patientId,
      provider: doctorId,
      ticket: ticketId,
      encounter_type: formData.encounter_type,
      encounter_date: formData.encounter_date,
      department: formData.department?.id || null,
      chief_complaint: formData.chief_complaint,
      diagnosis: formData.diagnosis,
      notes: formData.notes,
      status: formData.status,
    };

    handleAdd(submitData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{t("profile.addEncounter") || "Add Consultation/Encounter"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("profile.encounterType") || "Encounter Type"}</InputLabel>
              <Select
                value={formData.encounter_type}
                onChange={(e) =>
                  setFormData({ ...formData, encounter_type: e.target.value })
                }
                label={t("profile.encounterType") || "Encounter Type"}
              >
                <MenuItem value={1}>{t("profile.consultation") || "Consultation"}</MenuItem>
                <MenuItem value={2}>{t("profile.procedure") || "Procedure"}</MenuItem>
                <MenuItem value={3}>{t("profile.admission") || "Admission"}</MenuItem>
                <MenuItem value={4}>{t("profile.emergency") || "Emergency"}</MenuItem>
                <MenuItem value={5}>{t("profile.followUp") || "Follow-up"}</MenuItem>
                <MenuItem value={6}>{t("profile.diagnostic") || "Diagnostic"}</MenuItem>
                <MenuItem value={7}>{t("profile.other") || "Other"}</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("profile.dateTime") || "Date & Time"}
              type="datetime-local"
              value={formData.encounter_date}
              onChange={(e) =>
                setFormData({ ...formData, encounter_date: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <Autocomplete
              options={departments}
              getOptionLabel={(option) => option.name || ""}
              value={formData.department}
              onChange={(e, newValue) =>
                setFormData({ ...formData, department: newValue })
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("profile.department") || "Department"}
                  size="small"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("profile.chiefComplaint") || "Chief Complaint"}
              multiline
              rows={2}
              value={formData.chief_complaint}
              onChange={(e) =>
                setFormData({ ...formData, chief_complaint: e.target.value })
              }
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("profile.diagnosis") || "Diagnosis"}
              multiline
              rows={2}
              value={formData.diagnosis}
              onChange={(e) =>
                setFormData({ ...formData, diagnosis: e.target.value })
              }
              size="small"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label={t("profile.notes") || "Notes"}
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("profile.cancel") || "Cancel"}
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading
            ? t("profile.saving") || "Saving..."
            : t("profile.add") || "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EncounterDialog;
